/**
 * manifestView.ts — how the one derivation gets DRAWN.
 *
 * `computeSetupState` decides what is true; this decides what that looks like as a column of
 * rows, a set of counters and a footer. The split is deliberate and it is where the
 * previous screen went wrong: every presentation rule that lived inside a component was a
 * rule nobody could test, and the four surfaces that each re-derived "is this complete"
 * disagreed in production. Nothing here reads state — a row goes in, a mark comes out.
 *
 * IT IS THE SHELL'S VIEW RATHER THAN THE CONTRACT'S, AND THE TYPES SAY SO. The advance dossier
 * draws the same column over a `loans` row, so every function here reads a row or a group of
 * WHATEVER area vocabulary it was built with (`ManifestRow<Area, Id>`, defaulting to the
 * contract's). The marks, the counter and the open/closed rule are one implementation for both
 * rails — two spellings of `markFor` is how two rails come to draw the same state differently.
 * The footer below is the exception and stays concrete: activation is the contract's own ceremony.
 *
 * PURE, AND REACT-FREE, because vitest runs node-env over `.ts` only.
 */

import { format, isValid, parseISO } from 'date-fns';

import {
    activationVerdict,
    isRowSettled,
    type ActivationGate,
    type AreaGroup,
    type ManifestRow,
    type SetupProgress,
} from '../setupState';

/**
 * The four state tokens, plus the two modifiers the direction allows.
 *
 * `record` is not a fifth state — it is the DORMANT mark, worn by a row that is not on file and
 * cannot be actioned at all. EXACTLY ONE CONTRACT SHAPE WEARS IT: the contract that took no deposit
 * [D-B41], where both deposit rows go quiet. The insurance-loan agreement on a contract with no
 * loan is NOT one of them and has not been since 2026-08-04 — it is counted and blue there, because
 * a premium recorded on that contract may be financed and a financed one has to be papered.
 * `draft` and `refused` are modifiers over whatever the row would otherwise be.
 */
export type MarkKind = 'move' | 'wait' | 'locked' | 'done' | 'record' | 'draft' | 'refused';

/**
 * The same fact as a word.
 *
 * A 6px dot that means "somebody else is holding this up" has to survive not being seen, so
 * every row prints its state for screen readers beside the mark.
 *
 * `record` NO LONGER SAYS "Not on file", AND THAT IS THE POINT OF THE CHANGE. Half the rows that
 * are not on file now wear the blue dot, so "not on file" stopped being the thing this mark
 * distinguishes — the row's own detail says that. What is left to say is that there is nothing to
 * be done about it yet, which is exactly what separates it from every other unfilled row.
 */
export const MARK_WORDS: Record<MarkKind, string> = {
    move: 'Your move',
    wait: 'Waiting on somebody else',
    locked: 'Locked',
    done: 'Done',
    record: 'Nothing to do here yet',
    draft: 'Unsaved draft',
    refused: 'Refused by the server',
};

/**
 * The mark a row wears.
 *
 * `done` IS NOT DECIDED HERE. `isRowSettled` owns it, and the group counter reads the same
 * function — the two used to answer separately, and a record that is not on file was drawn as
 * `Not on file` while being counted as finished work (`Insurance 3/4` over four empty rows).
 *
 * ORDER OF PRECEDENCE IS THE POINT FOR THE REST. A refusal outranks everything — the server has
 * just said this row is why the contract did not activate. An unsaved draft outranks the saved
 * value beneath it, because the number on screen is not the number the agreement would print.
 * Only then does the row's own state speak.
 *
 * ── THE BLUE DOT IS "ACTIONABLE AND NOT YET PROVIDED", NOT "GATE" ────────────────────────────
 *
 * The owner's rule, from the insurance section and stated for all six: an item that has not been
 * provided yet and that the admin is the one to provide wears the your-move dot. Recording the
 * premium, recording the loan, filling in the driver file — none of them is a gate, all three are
 * the admin's own typing, and drawing them in the same muted grey as a row that genuinely cannot
 * be touched said the opposite. `Not on file` was being read as `nothing to do here`.
 *
 * SO THE GREY DOT SURVIVES FOR EXACTLY ONE THING: the row that cannot be actioned at all. The
 * insurance-loan agreement stopped being one on 2026-08-04 (owner's ruling) — a premium recorded on
 * a contract may be financed, and a financed one is refused until its agreement is sealed [D-B14],
 * so an admin looking at that row before the loan exists has work in front of them. THE TWO DEPOSIT
 * ROWS ARE ONE AGAIN ON THE CONTRACT THAT TOOK NO DEPOSIT [D-B41]: `sign_contract` no longer refuses
 * over the absence and the Schedule credits GHS 0.00 without one, so there is no send to unblock and
 * no paper to chase — nothing to do here yet, which is what this mark says and the only thing it has
 * ever said. `optional` IS the predicate rather than a proxy for it (`rowOptionalOf` sets it per
 * contract); the flag is READ here and never PRINTED anywhere — no row may wear the word "optional".
 *
 * THE COUNTERS DO NOT MOVE, AND THE INVARIANT THAT KEEPS THEM STILL IS WORTH THE SENTENCE: every
 * row this branch re-marks was already NOT settled (`isRowSettled` returns false for a record that
 * is not on file, which is why the first line above did not claim it), so the numerator is
 * untouched, and `isCountedRow` reads `optional` — the same flag this branch splits on — so the
 * denominator is untouched too. What comes out is a rail where the gap between the `1` and the `3`
 * of `1/3` is drawn as two dots an admin can go and act on, and every grey dot left on the column
 * is a row the counter was never counting.
 */
export function markFor(row: ManifestRow<string, string>): MarkKind {
    if (isRowSettled(row)) return 'done';
    if (row.refusal) return 'refused';
    if (row.meta.draft) return 'draft';
    // A record that is not on file — the only way a row reaches here still reading `done`.
    if (row.state === 'done') return row.optional ? 'record' : 'move';
    if (row.state === 'waiting') return 'wait';
    if (row.state === 'locked') return 'locked';
    return 'move';
}

/** The right-hand annotation: the row's own value, or the fact that it was refused. */
export function rowMeta(row: ManifestRow<string, string>): string {
    return row.refusal ? 'Refused' : row.detail;
}

/**
 * THE ONE KEY COMPARISON, and the reason it is a named function.
 *
 * The column asks two questions of every group — is it drawn OPEN, and does it carry the brand
 * MARKER — and for a while it answered them two different ways: this key match for the expansion,
 * and a measured DOM offset for the marker. The two are supposed to name the same group, and the
 * day the page's identity block moved into the manifest's scroller they stopped: the group holding
 * the route was expanded correctly while the marker sat two groups below it, displaced by exactly
 * the height of that block. Both derivations read this now, so they cannot disagree again.
 */
export function isActiveArea<Area extends string, Id extends string>(
    group: AreaGroup<Area, Id>,
    activeArea: Area | null,
): boolean {
    return group.area === activeArea;
}

/**
 * Whether a group is drawn open.
 *
 * A SETTLED MANIFEST MUST NOT EXCEED A SCREEN — that is the stated mitigation for this
 * direction's one real risk, a 21-row rail nobody reads. So a satisfied group collapses to its
 * dated receipt, the group holding the current route is always open regardless, and an explicit
 * toggle by the admin beats both.
 */
export function isGroupOpen<Area extends string, Id extends string>(
    group: AreaGroup<Area, Id>,
    activeArea: Area | null,
    overrides: Partial<Record<Area, boolean>> = {},
): boolean {
    const override = overrides[group.area];
    if (override !== undefined) return override;
    if (isActiveArea(group, activeArea)) return true;
    return !group.satisfied;
}

/** Where inside a group the brand marker lives — on its head, on one of its rows, or nowhere. */
export type MarkerHome = 'head' | 'row' | null;

/**
 * WHICH ELEMENT WEARS THE 2px BRAND MARKER, by key.
 *
 * The marker is rendered as a CHILD of whatever this names — the active area's group head, or the
 * selected row inside it — so it is structurally incapable of landing anywhere else. It carries no
 * offset, no measured `top` and no second geometry that has to be kept in step with the column's
 * composition, which is exactly what broke: an absolutely positioned bar whose `top` came from
 * `offsetTop` (counted from the scroller's padding edge) while its own static position started
 * below the identity block, so the block's height was paid twice and the marker named the wrong
 * group on every route.
 *
 * A COLLAPSED ACTIVE GROUP KEEPS ITS MARKER, on its head. An admin who folds the group they are
 * standing in has hidden its rows, not left the area, and the column must still say where they
 * are. The row branch is deliberately conditional on both `open` and membership: a selected row
 * that is not drawn — folded away, or left behind by a route change — hands the marker back to the
 * head rather than taking it off the screen.
 */
export function markerHome<Area extends string, Id extends string>(
    group: AreaGroup<Area, Id>,
    activeArea: Area | null,
    activeRowId: Id | null,
    open: boolean,
): MarkerHome {
    if (!isActiveArea(group, activeArea)) return null;
    if (open && activeRowId && group.rows.some((row) => row.id === activeRowId)) return 'row';
    return 'head';
}

/**
 * `3/5` — THE ROWS THIS GROUP DRAWS, done over total.
 *
 * The denominator is every non-dormant row in the group, so it is the list the admin is looking
 * at: only a dormant document is out of it, because a document whose subject does not exist and
 * which nothing on this contract owes cannot look overdue.
 * The numerator is `isRowSettled`, the same predicate `markFor` draws the dots from — so this can
 * neither say `3/4` over four rows that all read empty, nor `0/1` over five visible lines.
 *
 * A SATISFIED GROUP DOES NOT PRINT THIS AT ALL: it is collapsed to its dated receipt, and
 * `Manifest.tsx` prints the receipt in the counter's place. `1/4` appears only where somebody
 * opened the group, with the four rows on screen underneath it.
 */
export function groupCounter(group: AreaGroup<string, string>): string {
    return `${group.doneCount}/${group.requiredCount}`;
}

/**
 * The persistent sheet trigger below 1024px. Never an icon, never a hover reveal.
 *
 * ONE NUMBER, AND ONLY BECAUSE A BUTTON HAS TO SAY SOMETHING. The column's standing head used to
 * print three of them — areas complete, items needing the admin, items on somebody else — above
 * a list that says all three in the specific, row by row, with names attached. That head is
 * gone. What a closed sheet cannot show is how much is left inside it, so the trigger keeps the
 * outstanding count and nothing else; it is never a percentage, which is not auditable and
 * cannot be argued with.
 */
export function journeyLabel(progress: SetupProgress): string {
    return `Journey · ${progress.outstanding} open`;
}

export type GateTone = 'muted' | 'ready' | 'bad';

export interface GateFooter {
    note: string;
    tone: GateTone;
    label: string;
    disabled: boolean;
    /** What the button does: open the confirm surface, or re-open the receipt. */
    intent: 'activate' | 'receipt';
}

function dateTimeText(value: string | Date | null | undefined): string {
    if (!value) return '';
    const date = value instanceof Date ? value : parseISO(value);
    if (!isValid(date)) return '';
    return `${format(date, 'MMM d, yyyy')} at ${format(date, 'HH:mm')}`;
}

/**
 * The pinned footer, from day one.
 *
 * THE CONTROL IS NEVER HIDDEN, AND UNTIL NOW IT NEVER PRE-REFUSED. It sits there all week reading
 * `4 gates unmet` and it stayed clickable, on a rule worth restating because this function is
 * where it changes: the client did not own the gate list, so a button greying itself out on a
 * client-side COUNT was how a page ended up refusing work the server would have accepted.
 *
 * `can_activate` IS NOT A CLIENT-SIDE COUNT. It is the server's own answer to the exact question
 * the button asks, computed from live rows on every call, and relaying it is the opposite of
 * second-guessing the server — it is stopping the screen from offering a call the server has
 * already said it would decline.
 *
 * ⚠ AND FOR ONE RULE, TODAY, IT IS THE ONLY ENFORCEMENT THERE IS. `20260802020000` — the migration
 * that produces `can_activate` — is deliberately additive: it does NOT change `sign_contract`, and
 * its own post-conditions abort if it has. Making the activation path consume this list is
 * `20260802030000`, which has not landed. So in the window between the two, a contract blocked
 * ONLY by the insurance-loan agreement [D-B14] is refused HERE and would be accepted by the server
 * — 170 of the 196 contracts carrying a loan are in exactly that state. This branch is what makes
 * the decision real in the meantime, and it is written to survive its own obsolescence: when
 * `sign_contract` starts refusing on the same list the two agree, this stops being the only
 * enforcement, and nothing here has to change. The refusal mapping in `signContract.ts` already
 * reads both the sentences today's server produces and the codes tomorrow's will, for the same
 * reason.
 *
 * A REFUSAL OUTRANKS THE COUNT. If the server has just refused while the client believed every
 * gate met, saying "All 8 gates met." underneath would be the page arguing with the only
 * authority that matters.
 */
export function gateFooter(
    gate: ActivationGate,
    gateCount: number,
    activatedAt?: string | Date | null,
): GateFooter {
    if (gate.activated) {
        const when = dateTimeText(activatedAt);
        return {
            note: when ? `Activated ${when}` : 'This contract is active',
            tone: 'ready',
            label: 'Open the receipt',
            disabled: false,
            intent: 'receipt',
        };
    }

    if (gate.sealPending) {
        return {
            note: `Sealing the executed PDF — this finishes on its own · ${gate.sealPending.elapsedSeconds}s`,
            tone: 'muted',
            label: 'Activate contract',
            disabled: true,
            intent: 'activate',
        };
    }

    // THE VERDICT IS SHARED WITH THE HEADER BAND AND IS NOT RE-DERIVED HERE. `narratorFor` asks
    // `activationVerdict` the same question at the same point in its own precedence, which is what
    // stops the two Activate surfaces disagreeing — the live defect on contract 309, where this
    // footer was correctly dead under the server's sentence while the band above it read "Every
    // gate is met" beside a live button. Null-until-answered lives inside the verdict now.
    const verdict = activationVerdict(gate);

    if (gate.refusals.length > 0) {
        const onRows = gate.refusals.filter((refusal) => !!refusal.rowId).length;
        const note =
            onRows === 0
                ? gate.refusals[0].message
                : onRows === 1
                    ? 'Activation refused · 1 reason is on its row'
                    : `Activation refused · ${onRows} reasons are on their rows`;
        return { note, tone: 'bad', label: 'Activate contract', disabled: verdict.blocked, intent: 'activate' };
    }

    if (gate.unmetCount === 0) {
        // EVERY GATE ROW MET AND THE SERVER STILL SAYS NO. That is not a contradiction — most of
        // the payload's arms have no gate row of their own (the repayment tag, the driver profile,
        // the premium, the discount), so the rail can be clean while the contract is not ready.
        // Naming the server's reason is the whole difference between a disabled button and a
        // disabled button an admin can do something about.
        if (verdict.blocked) {
            return {
                note: verdict.reason,
                tone: 'bad',
                label: 'Activate contract',
                disabled: true,
                intent: 'activate',
            };
        }
        return {
            note: `All ${gateCount} gates met.`,
            tone: 'ready',
            label: 'Activate contract',
            disabled: false,
            intent: 'activate',
        };
    }

    return {
        note: `${gate.unmetCount} ${gate.unmetCount === 1 ? 'gate' : 'gates'} unmet`,
        tone: 'muted',
        label: 'Activate contract',
        disabled: verdict.blocked,
        intent: 'activate',
    };
}
